import React, { useState, Component } from 'react';
/**
 * AnswerComparison — Shows user answer vs correct answer in various modes.
 *
 * Modes: full (side-by-side cards), compact (stacked), inline (minimal).
 * Supports LaTeX rendering and long-answer truncation.
 *
 * Co-located in practice/ domain (CL-1 s64).
 */
import { motion } from 'framer-motion';
import { Check, X, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
// ── Types ────────────────────────────────────────────────────
interface AnswerComparisonProps {
  userAnswer: string;
  correctAnswer: string;
  isCorrect: boolean;
  mode?: 'full' | 'compact' | 'inline';
}
// ── Truncation Helper ────────────────────────────────────────
const TRUNCATE_LIMIT = 50;
function TruncatedAnswer({
  answer,
  variant
}: {
  answer: string;
  variant: 'correct' | 'incorrect';
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const needsTruncation = answer.length > TRUNCATE_LIMIT;
  const displayText = needsTruncation && !isExpanded ? answer.slice(0, TRUNCATE_LIMIT) + '...' : answer;
  const styles = variant === 'correct' ? {
    bg: 'bg-emerald-50 border-emerald-200',
    text: 'text-emerald-900',
    btn: 'text-emerald-600',
    border: 'border-emerald-100'
  } : {
    bg: 'bg-rose-50 border-rose-200',
    text: 'text-rose-900',
    btn: 'text-rose-600',
    border: 'border-rose-100'
  };
  return <div className={`rounded-xl border ${styles.bg} overflow-hidden`} data-id="element-10624" data-mp="82obn225kg97">
      <div className="px-3 py-2.5" data-id="element-10625" data-mp="d0zlvdwd0sry">
        <div className={`font-bold text-sm ${styles.text}`} data-id="element-10626" data-mp="czxzwedt2lvg">
          <LatexRenderer content={displayText} data-id="element-10627" data-mp="hr6bnh2lyjbs" />
        </div>
      </div>
      {needsTruncation && <button onClick={() => setIsExpanded(!isExpanded)} className={`w-full px-3 py-1.5 text-[10px] font-bold ${styles.btn} bg-white/50 border-t ${styles.border} flex items-center justify-center gap-1 hover:bg-white/80 transition-colors`} data-id="element-10628" data-mp="3ghzo3y3ky97s">
          {isExpanded ? <>
              Toon minder <ChevronUp className="w-3 h-3" data-id="element-10629" data-mp="nm6i91spvoqj" />
            </> : <>
              Toon meer <ChevronDown className="w-3 h-3" data-id="element-10630" data-mp="11d2zxxl3ulit" />
            </>}
        </button>}
    </div>;
}
// ── Component ────────────────────────────────────────────────
export function AnswerComparison({
  userAnswer,
  correctAnswer,
  isCorrect,
  mode = 'full'
}: AnswerComparisonProps) {
  // ── Inline Mode ──
  if (mode === 'inline') {
    if (isCorrect) {
      return <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="flex justify-center" data-id="element-10631" data-mp="ioy1n5n7vsre">
          <div className="inline-flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-3 shadow-sm max-w-full" data-id="element-10632" data-mp="1p3fhm065jwml">
            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0" data-id="element-10633" data-mp="21mb29xaxf1ht">
              <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-10634" data-mp="1nnbfkv6z4woa" />
            </div>
            <div className="font-bold text-emerald-900 text-base truncate max-w-[220px]" data-id="element-10635" data-mp="1slmjyqei2ez4">
              <LatexRenderer content={userAnswer.length > TRUNCATE_LIMIT ? userAnswer.slice(0, TRUNCATE_LIMIT) + '...' : userAnswer} data-id="element-10636" data-mp="2gbh13k5uqpp6" />
            </div>
          </div>
        </motion.div>;
    }
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0,
      x: [0, -6, 5, -4, 3, -1, 0]
    }} transition={{
      duration: 0.3,
      ease: 'easeOut',
      x: {
        duration: 0.4,
        delay: 0.1
      }
    }} className="space-y-2.5 px-2" data-id="element-10637" data-mp="2bk77k8h6n280">
        <div className="flex items-start gap-2" data-id="element-10638" data-mp="1bddkvca3idev">
          <div className="w-5 h-5 rounded-full bg-rose-200 flex items-center justify-center text-rose-700 flex-shrink-0 mt-2.5" data-id="element-10639" data-mp="1bfq0hisdsbor">
            <X className="w-3.5 h-3.5" strokeWidth={3} data-id="element-10640" data-mp="11fpf7rayblhx" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-10641" data-mp="11hk2vddlxrpp">
            <p className="text-[10px] font-bold uppercase text-rose-500 mb-1" data-id="element-10642" data-mp="1z3hbmj26qiog">
              Jouw antwoord
            </p>
            <TruncatedAnswer answer={userAnswer} variant="incorrect" data-id="element-10643" data-mp="1u5ftc0cl2ne1" />
          </div>
        </div>
        <div className="flex items-start gap-2" data-id="element-10644" data-mp="1pc3genh9o514">
          <div className="w-5 h-5 rounded-full bg-emerald-200 flex items-center justify-center text-emerald-700 flex-shrink-0 mt-2.5" data-id="element-10645" data-mp="1kebf52uqf6k9">
            <Check className="w-3.5 h-3.5" strokeWidth={3} data-id="element-10646" data-mp="2huqxlrt56zwx" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-10647" data-mp="2d2h1fu1fp3m7">
            <p className="text-[10px] font-bold uppercase text-emerald-600 mb-1" data-id="element-10648" data-mp="2d1ednuzerhai">
              Correct
            </p>
            <TruncatedAnswer answer={correctAnswer} variant="correct" data-id="element-10649" data-mp="2kg88g86i0e8t" />
          </div>
        </div>
      </motion.div>;
  }
  // ── Compact Mode ──
  if (mode === 'compact') {
    return <div className="grid gap-2" data-id="element-10650" data-mp="2uee6uft3j28x">
        <div className={`p-3 rounded-xl border-2 ${isCorrect ? 'border-emerald-200 bg-emerald-50' : 'border-rose-100 bg-rose-50'}`} data-id="element-10651" data-mp="2zb4i8ptyh9o0">
          <div className="text-[10px] font-black uppercase mb-1 opacity-60" data-id="element-10652" data-mp="33cpuc8yo1jx5">
            Jouw antwoord
          </div>
          <div className="font-bold text-slate-900 text-sm" data-id="element-10653" data-mp="389pz8gjs4gmo">
            <LatexRenderer content={userAnswer} data-id="element-10654" data-mp="3d43xqrtqn3vk" />
          </div>
        </div>
        {!isCorrect && <div className="p-3 rounded-xl border-2 border-emerald-200 bg-emerald-50" data-id="element-10655" data-mp="3i1cjf2e3hq1k">
            <div className="text-[10px] font-black uppercase mb-1 text-emerald-700" data-id="element-10656" data-mp="3n20bzshz2exw">
              Juiste antwoord
            </div>
            <div className="font-bold text-emerald-900 text-sm" data-id="element-10657" data-mp="3r0hic9tkjle9">
              <LatexRenderer content={correctAnswer} data-id="element-10658" data-mp="1ryvf36l30uak" />
            </div>
          </div>}
      </div>;
  }
  // ── Full Mode ──
  if (isCorrect) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1]
    }} className="bg-emerald-50 rounded-3xl p-6 border-2 border-emerald-200 shadow-sm" data-id="element-10659" data-mp="20qnorf2a7ze3">
        <div className="flex items-center gap-4" data-id="element-10660" data-mp="25oyvhgxa5ts3">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: [0, 1.2, 1]
        }} transition={{
          delay: 0.1,
          duration: 0.4,
          times: [0, 0.6, 1]
        }} className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-md flex-shrink-0" data-id="element-10661" data-mp="2jp9qmw5mhuj2">
            <Check className="w-7 h-7 text-white" strokeWidth={3} data-id="element-10662" data-mp="b6i03om4e5se" />
          </motion.div>
          <div data-id="element-10663" data-mp="f7aail6qagq9">
            <div className="text-xs font-black uppercase tracking-wider text-emerald-600 mb-1" data-id="element-10664" data-mp="f5qsoq5fbxmd">
              Jouw antwoord
            </div>
            <div className="font-black text-2xl text-emerald-900" data-id="element-10665" data-mp="6eqgjy1dt0br">
              <LatexRenderer content={userAnswer} data-id="element-10666" data-mp="6d5i3xby4x8m" />
            </div>
          </div>
        </div>
      </motion.div>;
  }
  return <div className="flex flex-col md:flex-row gap-3 items-stretch" data-id="element-10667" data-mp="3slx4isgcjj7h">
      <motion.div initial={{
      opacity: 0,
      x: -16
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className="flex-1" data-id="element-10668" data-mp="3sn87wr5vqoy2">
        <div className="bg-rose-50 rounded-3xl p-6 border-2 border-rose-200 h-full shadow-sm" data-id="element-10669" data-mp="1cvbxp2qtzp3j">
          <div className="flex items-center gap-3 mb-3" data-id="element-10670" data-mp="1hskeaywbzl2o">
            <div className="w-9 h-9 rounded-xl bg-rose-200 flex items-center justify-center text-rose-700 shadow-sm flex-shrink-0" data-id="element-10671" data-mp="2qe1epjfe5ai9">
              <X className="w-5 h-5" strokeWidth={3} data-id="element-10672" data-mp="2lgj31l9zoh6k" />
            </div>
            <span className="text-xs font-black uppercase tracking-wider text-rose-600" data-id="element-10673" data-mp="2gnzoom3h9i0p">
              Jouw antwoord
            </span>
          </div>
          <div className="font-black text-2xl text-rose-900 pl-12" data-id="element-10674" data-mp="2cmnzghoimyt1">
            <LatexRenderer content={userAnswer} data-id="element-10675" data-mp="27ouq5frx1ooj" />
          </div>
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 0.15,
      duration: 0.3
    }} className="hidden md:flex items-center justify-center flex-shrink-0" data-id="element-10676" data-mp="22nownfqflxdy">
        <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center" data-id="element-10677" data-mp="3s2vdv1zr31vi">
          <ArrowRight className="w-5 h-5 text-slate-400" strokeWidth={2.5} data-id="element-10678" data-mp="3o08k2vjir6vo" />
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      x: 16
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.1,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className="flex-1" data-id="element-10679" data-mp="1etet47d6o220">
        <div className="bg-emerald-50 rounded-3xl p-6 border-2 border-emerald-200 h-full shadow-sm" data-id="element-10680" data-mp="qvplf3m1fwo0">
          <div className="flex items-center gap-3 mb-3" data-id="element-10681" data-mp="vu97r54jnagw">
            <div className="w-9 h-9 rounded-xl bg-emerald-200 flex items-center justify-center text-emerald-700 shadow-sm flex-shrink-0" data-id="element-10682" data-mp="vuss0sho2ay3">
              <Check className="w-5 h-5" strokeWidth={3} data-id="element-10683" data-mp="14uz4qc5jj6w7" />
            </div>
            <span className="text-xs font-black uppercase tracking-wider text-emerald-600" data-id="element-10684" data-mp="14supiy26ylgm">
              Het juiste antwoord
            </span>
          </div>
          <div className="font-black text-2xl text-emerald-900 pl-12" data-id="element-10685" data-mp="2g92ut790cavz">
            <LatexRenderer content={correctAnswer} data-id="element-10686" data-mp="1w9v7ealbv3ms" />
          </div>
        </div>
      </motion.div>
    </div>;
}